import * as React from "react";
import {
  DataGridPro,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarColumnsButton,
  GridColumnMenu,
} from "@mui/x-data-grid-pro";
import AgGridListing from "./agGridListing";
import axios from "axios";
import { Box, Button, IconButton, Tooltip, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { tokens } from "../../theme";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import ErrorAlert from "helper/errorAlert";
import { useApiClient } from "context/ApiClientContext";

// Toggle this boolean to switch between the original MUI Grid (false) and the new free AG Grid Community (true)
const USE_AG_GRID = true;

const StyledGridOverlay = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  "& .ant-empty-img-1": {
    fill: theme.palette.mode === "light" ? "#aeb8c2" : "#262626",
  },
  "& .ant-empty-img-2": {
    fill: theme.palette.mode === "light" ? "#f5f5f7" : "#595959",
  },
  "& .ant-empty-img-3": {
    fill: theme.palette.mode === "light" ? "#dce0e6" : "#434343",
  },
  "& .ant-empty-img-4": {
    fill: theme.palette.mode === "light" ? "#fff" : "#1c1c1c",
  },
  "& .ant-empty-img-5": {
    fillOpacity: theme.palette.mode === "light" ? "0.8" : "0.08",
    fill: theme.palette.mode === "light" ? "#f5f5f5" : "#fff",
  },
}));

function CustomNoRowsOverlay() {
  return (
    <StyledGridOverlay>
      <svg
        style={{ flexShrink: 0 }}
        width="240"
        height="200"
        viewBox="0 0 184 152"
        aria-hidden
        focusable="false"
      >
        <g fill="none" fillRule="evenodd">
          <g transform="translate(24 31.67)">
            <ellipse
              className="ant-empty-img-5"
              cx="67.797"
              cy="106.89"
              rx="67.797"
              ry="12.668"
            />
            <path
              className="ant-empty-img-1"
              d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
            />
            <path
              className="ant-empty-img-2"
              d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
            />
            <path
              className="ant-empty-img-3"
              d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z"
            />
          </g>
          <path
            className="ant-empty-img-3"
            d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z"
          />
          <g className="ant-empty-img-4" transform="translate(149.65 15.383)">
            <ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" />
            <path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" />
          </g>
        </g>
      </svg>
      <Box sx={{ mt: 1 }}>No Rows</Box>
    </StyledGridOverlay>
  );
}

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <Box sx={{ flexGrow: 1 }} />
    </GridToolbarContainer>
  );
}

function CustomColumnMenu(props) {
  return (
    <GridColumnMenu
      {...props}
      slots={{
        // Hide `columnMenuColumnsItem`
        columnMenuColumnsItem: null,
        columnMenuSortItem: null,
        columnMenuFilterItem: null,
      }}
    />
  );
}

export default function Listing({ setShowCompany }) {
  const [response, setResponse] = React.useState([]);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const apiClient = useApiClient();
  
  const columns = [
    { field: "name", headerName: "Company Name", flex: 1 },
    { field: "code", headerName: "ERP ID", width: 180 },
    {
      field: "irbclientid",
      headerName: "Client Id",
      flex: 1,
      valueGetter: ({ row }) => row?.irbclientid || "-",
    },
    {
      field: "irbsecret",
      headerName: "Client Secret",
      flex: 1,
      valueGetter: ({ row }) => row?.irbsecret || "-",
    },
    {
      field: "tin_number",
      headerName: "Tin Number",
      flex: 1,
      valueGetter: ({ row }) => row?.tin_number || "-",
    },
    { field: "emailreceiver", headerName: "Email", flex: 1, 
    valueGetter: ({ row }) =>
      row?.emailreceiver || "-"

     },
    {
      field: "invnumsource",
      headerName: "Invoice Option",
      flex: 1,
      valueGetter: ({ row }) => row?.invnumsource || "-",
    },
    { field: "intermediary", headerName: "Intermediary", type: "boolean", width: 120 },
    { field: "isActive", headerName: "Active", type: "boolean", width: 80 },
    {
      field: "Actions",
      headerName: "Action",
      type: "actions",
      width: 80,
      hideable: false,
      getActions: (params) => [
        // <Link to={`/company-details/${params.id}`}>
        <IconButton
          color="primary"
          size="small"
          onClick={handleShowUser(params.id)}
        >
          <Tooltip title="Edit Company" placement="right">
            <BorderColorIcon />
          </Tooltip>
        </IconButton>,
        // </Link>
      ],
    },
  ];

  const fetchDataAndUpdateList = async (i) => {
    try {
      const response = await axios.get(
        `${apiClient}/company?sortcriteria=id%20desc`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            Accept: "application/json",
          },
        }
      );
      setResponse(response?.data);
    } catch (error) {
      console.error("An error occurred:", error.response);
    }
  };

  React.useEffect(() => {
    fetchDataAndUpdateList();
  }, []);

  const handleShowUser = (id) => () => {
    setShowCompany(id); // Pass the user's ID to the parent component
  };
  return (
    <Box
      style={{ height: 600, width: "100%" }}
      sx={{
        "& .MuiDataGrid-root": {
          border: "none",
          display: "flex",
          justifyContent: "space-around",
        },
        "& .MuiDataGrid-cell": {
          borderBottom: "none",
        },
        "& .name-column--cell": {
          color: colors.greenAccent[300],
          borderBottom: "none",
        },
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: colors.grey[600],
          color: `${colors.primary[100]} !important`,
          borderBottom: "none",
        },
        "& .MuiDataGrid-virtualScroller": {
          backgroundColor: colors.grey[800],
        },
        "& .MuiDataGrid-footerContainer": {
          borderTop: "none",
          backgroundColor: colors.grey[600],
        },
        "& .MuiCheckbox-root": {
          color: `${colors.grey[200]} !important`,
        },
        "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
          color: `${colors.grey[100]} !important`,
          display: "flex",
          justifyContent: "space-around",
        },
      }}
    >
      {USE_AG_GRID ? (
        <AgGridListing
          rowData={response}
          setShowCompany={setShowCompany}
        />
      ) : (
        <DataGridPro
          disableColumnFilter
          disableColumnSelector
          density="compact"
          rows={response}
          columns={columns}
          sx={{ "--DataGrid-overlayHeight": "300px", marginTop:"1%" }}
          slots={{
            toolbar: CustomToolbar,
            noRowsOverlay: CustomNoRowsOverlay,
            columnMenu: CustomColumnMenu,
          }}
          slotProps={{
            panel: { sx: { "& .MuiPaper-root": { maxHeight: "300px" } } },
            filterPanel: {
              columnsSort: "asc",
              filterFormProps: {
                logicOperatorInputProps: {
                  variant: "outlined",
                  size: "small",
                },
                columnInputProps: {
                  variant: "outlined",
                  size: "small",
                  sx: { mt: "auto" },
                },
                operatorInputProps: {
                  variant: "outlined",
                  size: "small",
                  sx: { mt: "auto" },
                },
                valueInputProps: {
                  InputComponentProps: {
                    variant: "outlined",
                    size: "small",
                  },
                },
                deleteIconProps: {
                  sx: {
                    "& .MuiSvgIcon-root": { color: "#d32f2f" },
                  },
                },
              },
              sx: {
                // Customize inputs using css selectors
                "& .MuiDataGrid-filterForm": { p: 2 },
                "& .MuiDataGrid-filterForm:nth-child(even)": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "#444" : "#f5f5f5",
                },
                "& .MuiDataGrid-filterFormLogicOperatorInput": { mr: 2 },
                "& .MuiDataGrid-filterFormColumnInput": { mr: 2, width: 150 },
                "& .MuiDataGrid-filterFormOperatorInput": { mr: 2 },
                "& .MuiDataGrid-filterFormValueInput": { width: 200 },
              },
            },
          }}
          pagination
          expand
        />
      )}
    </Box>
  );
}